import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import type { SdkKey } from '@gonfalon/sdk-info/sdkInfo';

import type { SdkKeySource } from './types';

export const trackSdkKeys = createTrackerForCategory('SDK Keys');

export const trackShowSdkKeys = () => trackSdkKeys('Show SDK Keys');
export const trackSdkKeysDialogClosed = () => trackSdkKeys('SDK Keys Dialog Closed');
export const trackSdkKeyConfigurationDocsLinkClicked = () =>
  trackSdkKeys('SDK Key Configuration Documentation Link Clicked');
export const trackSdkKeyCopied = ({ source }: { source: SdkKeySource }) => trackSdkKeys('SDK Key Copied', { source });
export const trackSdkKeyViewToggled = ({ source, isVisible }: { source: SdkKeySource; isVisible: boolean }) =>
  trackSdkKeys('SDK Key View Toggled', { source, isVisible: isVisible ? 'visible' : 'hidden' });
export const trackSdkKeyLabelInfoIconViewed = ({ source }: { source: SdkKeySource }) =>
  trackSdkKeys('SDK Key Label tooltip viewed', { source });
export const trackProjectSelectOpened = ({ isInProject }: { isInProject: boolean }) =>
  trackSdkKeys('Project Select Opened', { isInProject: isInProject ? 'true' : 'false' });
export const trackNewProjectSelected = ({ isInProject }: { isInProject: boolean }) =>
  trackSdkKeys('New Project Selected', { isInProject: isInProject ? 'true' : 'false' });
export const trackEnvironmentSelectOpened = ({ isInProject }: { isInProject: boolean }) =>
  trackSdkKeys('Environment Select Opened', { isInProject: isInProject ? 'true' : 'false' });
export const trackNewEnvironmentSelected = ({ isInProject }: { isInProject: boolean }) =>
  trackSdkKeys('New Environment Selected', { isInProject: isInProject ? 'true' : 'false' });
export const trackSdkSelected = ({ sdk, isInProject }: { sdk: SdkKey | null; isInProject: boolean }) =>
  trackSdkKeys('SDK Selected', { sdk: sdk ?? 'none', isInProject: isInProject ? 'true' : 'false' });

/**************************************************
 * Analytics for concurrent sdk keys
 **************************************************/

type SdkKeyEventAttributes = {
  kind?: 'sdk' | 'mobile';
  payload?: {
    type?: 'default' | 'custom';
    size?: number;
    previous?: Omit<SdkKeyEventAttributes['payload'], 'previous'>;
  };
};

export const trackNewSdkKeyButtonClicked = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('New SDK Key Button Clicked', {
    kind,
    cta: kind === 'mobile' ? 'Create mobile key' : 'Create SDK key',
  });

export const trackNewSdkKeyCreated = (attrs: SdkKeyEventAttributes) => trackSdkKeys('New SDK Key Created', attrs);

export const trackEditSdkKeyMenuItemClicked = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('Edit SDK Key Menu Item Clicked', { kind, cta: 'Edit' });

export const trackSdkKeyEdited = (attrs: SdkKeyEventAttributes) => trackSdkKeys('SDK Key Edited', attrs);

export const trackSdkKeyCustomPayloadBadgeClicked = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('SDK Key Custom Payload Badge Clicked', { kind, cta: 'Custom payload' });

export const trackSdkKeyCustomPayloadBadgeViewClicked = ({
  kind,
  viewKey,
}: {
  kind: SdkKeyEventAttributes['kind'];
  viewKey: string;
}) => trackSdkKeys('SDK Key Custom Payload Badge View Clicked', { kind, viewKey });

export const trackCloneSdkKeyMenuItemClicked = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('Clone SDK Key Menu Item Clicked', {
    kind,
    cta: 'Copy properties to new key',
  });

export const trackSdkKeyCloned = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('SDK Key Cloned', { kind });

export const trackSdkKeyExpirationMenuItemClicked = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('SDK Key Expiration Menu Item Clicked', { kind });

export const trackSdkKeyExpirationUpdated = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('SDK Key Expiration Updated', { kind });

export const trackSdkKeyDeleteMenuItemClicked = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('SDK Key Delete Menu Item Clicked', { kind });

export const trackSdkKeyDeleted = ({ kind }: { kind: SdkKeyEventAttributes['kind'] }) =>
  trackSdkKeys('SDK Key Deleted', { kind });

/**************************************************
 * Payload change review
 *
 * Emitted by the shared `PayloadChangeDialogBase` (used by the SDK key edit,
 * mobile SDK toggle, and view-resource change confirm flows). Kept under the
 * 'Views' analytics category so the event names/category are unchanged; they
 * live here rather than in `@gonfalon/views` because this package's dialog is
 * their only consumer — importing them from views creates a
 * chrome → sdk-keys → views → chrome workspace dependency cycle.
 **************************************************/

const trackViewEvent = createTrackerForCategory('Views');

export const trackReviewPayloadChangeConfirmed = () => trackViewEvent('Review View Payload Change Confirmed');
export const trackReviewPayloadChangeCancelled = () => trackViewEvent('Review View Payload Change Cancelled');
